import React, {useState} from 'react';
import {Alert, Box, Button, Grid, TextField} from '@mui/material';
import {useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import {Panel} from '../../components/Panel/Panel';
import {
  UpdateDeliveryPreferences,
  UpdateDeliveryPreferencesVariables,
} from '../../schema-types/UpdateDeliveryPreferences';
import {getErrorMessage} from '../../utils/getErrorMessage';
import validateUUID from '../../utils/validateUUID';
import {UPDATE_DELIVERY_PREFERENCES} from './deliveryPreferenceQueries';
import {DeliveryPreferences} from './deliveryPreferenceText';

export type DeliveryPreferencesModalContact = {id: string; simplifiedName: string | null};

type Choice = 'allow' | 'deny';

// Only an explicit "Do Not Allow" denies; a value not synced yet reads as Allow (DBO-1575).
const toChoice = (doNot: boolean | null): Choice => (doNot ? 'deny' : 'allow');

type Props = {
  open: boolean;
  onClose: () => void;
  /** Called after Curo accepted the change, before the modal closes. */
  onSaved: () => void;
  accountId: string;
  contact: DeliveryPreferencesModalContact;
  /** The values Curo holds now; a null part (not synced yet) reads as Allow. */
  current: DeliveryPreferences;
};

/**
 * "Client delivery preferences" dialog from the DBO-1575 mockup: Email and SMS,
 * each Allow or Do not allow, Cancel and Update. Update writes to Curo in the
 * same request, so a Curo rejection is shown here and nothing is left half-saved.
 */
export const DeliveryPreferencesModal: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  accountId,
  contact,
  current,
}) => {
  const initialEmail = toChoice(current.doNotEmail);
  const initialSms = toChoice(current.doNotSms);
  const [email, setEmail] = useState<Choice>(initialEmail);
  const [sms, setSms] = useState<Choice>(initialSms);
  const [errors, setErrors] = useState<string[]>([]);
  const {enqueueSnackbar} = useSnackbar();
  const [updateDeliveryPreferences, {loading}] = useMutation<
    UpdateDeliveryPreferences,
    UpdateDeliveryPreferencesVariables
  >(UPDATE_DELIVERY_PREFERENCES);

  // A contact not synced yet may be saved as shown, so OneView catches up with Curo.
  const synced = current.doNotEmail !== null && current.doNotSms !== null;
  const unchanged = synced && email === initialEmail && sms === initialSms;
  const canSubmit = !loading && !unchanged;

  const submit = async () => {
    setErrors([]);
    try {
      const {data} = await updateDeliveryPreferences({
        variables: {
          contactId: validateUUID(contact.id)!,
          accountId: validateUUID(accountId)!,
          isEmailAllowed: email === 'allow',
          isSmsAllowed: sms === 'allow',
        },
      });
      const result = data?.updateDeliveryPreferences;
      if (!result?.ok) {
        setErrors(
          result?.errors?.length
            ? result.errors
            : ['Could not save the delivery preferences.']
        );
        return;
      }
      enqueueSnackbar('Delivery preferences updated in Curo.', {variant: 'success'});
      onSaved();
      onClose();
    } catch (error) {
      setErrors([getErrorMessage(error, 'Could not reach the server.')]);
    }
  };

  const contactName = contact.simplifiedName || 'contact';
  const choiceField = (
    id: string,
    label: string,
    value: Choice,
    onChange: (choice: Choice) => void
  ) => (
    <TextField
      id={id}
      select
      SelectProps={{native: true}}
      label={label}
      fullWidth
      size="small"
      InputLabelProps={{shrink: true}}
      value={value}
      onChange={(event) => onChange(event.target.value as Choice)}
    >
      <option value="allow">Allow</option>
      <option value="deny">Do not allow</option>
    </TextField>
  );

  return (
    <ScrollableModal size="small" autoHeight open={open} onClose={onClose}>
      <Panel title={`Client delivery preferences: ${contactName}`}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {choiceField('delivery-preference-email', 'Email', email, setEmail)}
          </Grid>
          <Grid item xs={12}>
            {choiceField('delivery-preference-sms', 'SMS', sms, setSms)}
          </Grid>
        </Grid>
        {errors.length > 0 ? (
          <Alert severity="error" sx={{mt: 2}}>
            {errors.join(' ')}
          </Alert>
        ) : null}
        <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
          <Button variant="text" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!canSubmit}
            onClick={submit}
          >
            Update
          </Button>
        </Box>
      </Panel>
    </ScrollableModal>
  );
};
